import { type ReactNode, type ReactElement, useState } from 'react'
import { type CartPageCartItemMessageInfoFragment } from '@stuller/stullercom/data-access/apollo-queries'
import { CartItemContext } from './CartItemContext'
import type { CartLine } from '../../shared/types'
import { IMAGE_COMING_SOON } from '@stuller/shared/util/constants'
import { getItemUrl } from '@stuller/stullercom/util/core'

interface CartItemContextProviderProps {
  /**
   * Children to render inside the context provider
   */
  children: ReactNode
  /**
   * The whole cart line object
   */
  cartLine: CartLine
  /**
   * Disable input fields in the cart item
   */
  disableFormChanges?: boolean
}

/**
 * The context provider for the CartItem to help manage the state of the cart item options
 */
function CartItemContextProvider ({
  children,
  cartLine,
  disableFormChanges = false
}: CartItemContextProviderProps): ReactElement {
  const [loading, setLoading] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)
  const [error, setError] = useState<CartPageCartItemMessageInfoFragment[]>([])
  const isConfiguration = cartLine.item?.__typename === 'Configuration'
  const isSerializedProduct = cartLine.item?.__typename === 'SerializedProduct'
  const imageUrl = cartLine.item?.image ?? IMAGE_COMING_SOON
  const itemTitle = cartLine.item?.title ?? ''
  const itemUrl = cartLine.item?.__typename !== undefined ? getItemUrl(cartLine.item.__typename, cartLine.item.id, cartLine.productGroup?.id, 'Cart') : ''
  const itemNumber = isSerializedProduct ? `Serial #${cartLine.item?.id}` : cartLine.item?.itemNumber
  const canMatch = cartLine.item?.canMatch ?? false
  const isMatched = cartLine.options?.isMatched ?? false
  const canAnneal = cartLine.item?.canAnneal ?? false
  const isAnnealed = cartLine.options?.isAnnealed ?? false
  const canOrderOnConsignment = cartLine.item?.canOrderOnConsignment ?? false
  const isForConsignment = cartLine.options?.isForConsignment ?? false
  const isNonReturnable = cartLine.item?.isReturnable === false
  const isLimitedAvailability = cartLine.item?.isLimitedAvailability ?? false
  const canEditItem = cartLine.canEditItem ?? false

  return (
    <CartItemContext.Provider
      value={{
        cartLine,
        disableFormChanges: disableFormChanges || loading,
        loading,
        setLoading,
        success,
        setSuccess,
        error,
        setError,
        isConfiguration,
        isSerializedProduct,
        imageUrl,
        itemTitle,
        itemUrl,
        itemNumber,
        canMatch,
        isMatched,
        canAnneal,
        isAnnealed,
        canOrderOnConsignment,
        isForConsignment,
        isNonReturnable,
        isLimitedAvailability,
        canEditItem
      }}
    >
      {children}
    </CartItemContext.Provider>
  )
}

export {
  CartItemContextProvider
}
